import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Warning } from "@phosphor-icons/react";

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtKg = (n) => Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 });

/**
 * Modal de advertencia previa al cierre de una venta cuando uno o más
 * ítems fueron cobrados con un precio distinto a la lista asignada al
 * cliente. Usa Shadcn/Radix AlertDialog para que se stackee correctamente
 * arriba del PaymentDialog (POS) o del Sheet (Facturas) sin conflictos de
 * pointer-events / focus trap.
 */
export default function AlertaListaPrecioModal({
  open,
  onClose,
  onConfirm,
  cliente,
  listaClienteNombre,
  mismatches,
  actionLabel = "Facturar igual",
}) {
  const cantAjena = (mismatches || []).filter((m) => m.tipo === "lista_ajena").length;
  const cantFuera = (mismatches || []).filter((m) => m.tipo === "fuera_de_lista").length;
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <AlertDialogContent className="max-w-3xl" data-testid="alerta-lista-precio-modal">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-amber-900 flex items-center gap-2">
            <Warning size={18} weight="fill" className="text-amber-700" />
            Precio no corresponde a la lista del cliente
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[12px] font-mono">
            <span className="font-bold">{cliente?.nombre || "Cliente"}</span>
            {listaClienteNombre ? <> · lista asignada: <span className="font-bold">{listaClienteNombre}</span></> : null}
            {" · "}{cantAjena > 0 && `${cantAjena} con lista ajena`}
            {cantAjena > 0 && cantFuera > 0 && " · "}
            {cantFuera > 0 && `${cantFuera} fuera de toda lista`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="max-h-[55vh] overflow-y-auto border border-amber-200 rounded-sm">
          <table className="w-full text-[12px]">
            <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Producto</th>
                <th className="px-3 py-2 text-right">Kg / Un</th>
                <th className="px-3 py-2 text-right">Precio cobrado c/IVA</th>
                <th className="px-3 py-2 text-right">Precio esperado</th>
                <th className="px-3 py-2 text-right">Δ %</th>
                <th className="px-3 py-2 text-left">Diagnóstico</th>
              </tr>
            </thead>
            <tbody>
              {(mismatches || []).map((m, i) => (
                <tr key={i} className="border-t border-amber-100" data-testid={`alerta-lista-row-${i}`}>
                  <td className="px-3 py-1.5 font-medium">{m.descripcion}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtKg(m.kilos)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold">{fmtMoney(m.precio_facturado_con_iva)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {m.precio_lista_cliente != null ? fmtMoney(m.precio_lista_cliente) : "—"}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono font-bold ${
                    (m.diferencia_pct || 0) < 0 ? "text-rose-700" : "text-amber-700"
                  }`}>
                    {m.diferencia_pct != null ? `${m.diferencia_pct > 0 ? "+" : ""}${m.diferencia_pct}%` : "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    {m.tipo === "lista_ajena" ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-orange-100 text-orange-900 border border-orange-300">
                        Precio de lista {m.lista_detectada_nombre}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-rose-100 text-rose-900 border border-rose-300">
                        <Warning size={10} weight="fill" /> Fuera de toda lista
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="alerta-lista-modificar">
            Modificar venta
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="alerta-lista-confirmar"
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
